import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "../../styles/BeerFinderLayout.css";
import "../../styles/BeerFinderButtons.css";
import "../../styles/BeerFinderStart.css";
import "../../styles/BeerFinderQuestion.css";
import "../../styles/BeerFinderResult.css";
import "../../styles/BeerFinderProduct.css";

import { QUESTIONS } from "./beerFinder.questions";
import { buildAnswerTags, buildTasteProfile } from "./beerFinder.helpers";

import BeerFinderAuthScreen from "./BeerFinderAuthScreen";
import BeerFinderStartScreen from "./BeerFinderStartScreen";
import BeerFinderQuestionScreen from "./BeerFinderQuestionScreen";
import BeerFinderResultScreen from "./BeerFinderResultScreen";

export default function BeerFinder({
  onShowRecommendations,
  mode = "quiz",
  savedResult = null,
  isLoggedIn = false,
  onLogin,
  onOpenProduct,
}) {
  const { t } = useTranslation("beerFinder");

  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [error, setError] = useState("");

  const currentQuestion = QUESTIONS[step];
  const isLastStep = step === QUESTIONS.length - 1;
  const isFinished = started && step >= QUESTIONS.length;

  const savedAnswers = useMemo(() => savedResult?.answers || {}, [savedResult]);

  const profile = useMemo(() => {
    if (mode === "saved" && savedResult?.profile) {
      return savedResult.profile;
    }

    return buildTasteProfile(answers, t);
  }, [answers, mode, savedResult, t]);

  const answerTags = useMemo(() => {
    return buildAnswerTags(answers, t);
  }, [answers, t]);

  useEffect(() => {
    if (mode !== "saved") return;

    const savedRecommendations = savedResult?.recommendations || null;

    if (savedRecommendations) {
      setStarted(true);
      setStep(QUESTIONS.length);
      setAnswers(savedAnswers);
      setRecommendations(savedRecommendations);
      setError("");
      setLoadingRecommendations(false);
    }
  }, [mode, savedResult, savedAnswers]);

  useEffect(() => {
    if (mode !== "quiz") return;

    setStarted(false);
    setStep(0);
    setAnswers({});
    setRecommendations(null);
    setError("");
    setLoadingRecommendations(false);
  }, [mode]);

  const handleAnswer = (questionId, value) => {
    const nextAnswers = {
      ...answers,
      [questionId]: value,
    };

    setAnswers(nextAnswers);
    setError("");
    setRecommendations(null);

    if (isLastStep) {
      setStep(QUESTIONS.length);
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const handleRestart = () => {
    setStarted(false);
    setStep(0);
    setAnswers({});
    setRecommendations(null);
    setError("");
    setLoadingRecommendations(false);
  };

  const handleBack = () => {
    setRecommendations(null);
    setError("");
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const handleShowRecommendations = async () => {
    if (typeof onShowRecommendations !== "function") return;

    try {
      setError("");
      setLoadingRecommendations(true);

      const res = await onShowRecommendations({
        answers,
        profile,
      });

      setRecommendations(res || null);
    } catch (e) {
      console.error("beer finder error:", e);
      setError(t("error"));
    } finally {
      setLoadingRecommendations(false);
    }
  };

  if (!isLoggedIn) {
    return <BeerFinderAuthScreen t={t} onLogin={onLogin} />;
  }

  if (!started) {
    return <BeerFinderStartScreen t={t} onStart={() => setStarted(true)} />;
  }

  if (isFinished) {
    return (
      <BeerFinderResultScreen
        t={t}
        mode={mode}
        recommendations={recommendations}
        answerTags={answerTags}
        loadingRecommendations={loadingRecommendations}
        error={error}
        onShowRecommendations={handleShowRecommendations}
        onRestart={handleRestart}
        onOpenProduct={onOpenProduct}
      />
    );
  }

  return (
    <BeerFinderQuestionScreen
      t={t}
      step={step}
      total={QUESTIONS.length}
      question={currentQuestion}
      onAnswer={handleAnswer}
      onBack={handleBack}
    />
  );
}